// AllLearningSummary — 銀行 + 請求書 統合 KPI summary
// (= 学習機構 全体 dashboard)

import React, { useEffect, useState, useCallback } from 'react';
import {
  CardBase,
  Note,
  MessageBlock,
  SubSectionTitle,
  InlineSpinner,
} from '@freee_jp/vibes';
import {
  fetchLearningMetrics,
  fetchInvoiceLearningMetrics,
  LearningMetrics,
  InvoiceLearningMetrics,
} from '../../api/client';

const formatPct = (n: number | null | undefined) =>
  n == null ? '—' : `${(n * 100).toFixed(1)}%`;

export const AllLearningSummary: React.FC = () => {
  const [bank, setBank] = useState<LearningMetrics | null>(null);
  const [invoice, setInvoice] = useState<InvoiceLearningMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [bRes, iRes] = await Promise.allSettled([
      fetchLearningMetrics(30),
      fetchInvoiceLearningMetrics(30),
    ]);
    const errs: string[] = [];
    if (bRes.status === 'fulfilled') setBank(bRes.value);
    else errs.push(`bank: ${bRes.reason instanceof Error ? bRes.reason.message : String(bRes.reason)}`);
    if (iRes.status === 'fulfilled') setInvoice(iRes.value);
    else errs.push(`invoice: ${iRes.reason instanceof Error ? iRes.reason.message : String(iRes.reason)}`);
    if (errs.length) setError(errs.join(' / '));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Note>銀行取引 + 請求書 学習機構の統合サマリー (30日)</Note>
      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}
      {loading && <InlineSpinner isLoading={true} />}

      {/* 銀行 ベース KPI */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '8px 12px' }}>
          <SubSectionTitle headlineLevel={3}>銀行取引 学習</SubSectionTitle>
          <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <tbody>
              <Row label="AI skip 率" value={bank?.summary.ai_skip_rate_recent != null ? formatPct(bank.summary.ai_skip_rate_recent) : '—'} highlight="#7c5cff" />
              <Row label="節約 token (30日)" value={bank ? `${(bank.summary.token_saved_recent || 0).toLocaleString()} (≈ $${bank.summary.token_cost_saved_usd_recent})` : '—'} highlight="#338650" />
              <Row label="patterns 全件" value={bank?.pattern_stats.total_patterns || 0} />
              <Row label="高信頼 patterns" value={bank?.pattern_stats.mid_conf || 0} highlight="#338650" />
              <Row label="平均 confidence" value={formatPct(bank?.pattern_stats.avg_conf)} />
              <Row label="audit 違反 (RULE 50/51)" value={bank ? (bank.audit_violations.rule50_skips || 0) + (bank.audit_violations.rule51_skips || 0) : 0} highlight={bank && (bank.audit_violations.rule50_skips || 0) + (bank.audit_violations.rule51_skips || 0) > 0 ? '#c33939' : undefined} />
            </tbody>
          </table>
        </div>
      </CardBase>

      {/* 請求書 ベース KPI */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '8px 12px' }}>
          <SubSectionTitle headlineLevel={3}>請求書 学習</SubSectionTitle>
          <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <tbody>
              <Row label="ARICA 登録率" value={invoice?.arica.registered_rate != null ? formatPct(invoice.arica.registered_rate) : '—'} highlight="#338650" />
              <Row label="ARICA total / registered" value={invoice ? `${invoice.arica.total} / ${invoice.arica.registered}` : '—'} />
              <Row label="cadence 高信頼 (≥0.85)" value={invoice ? `${invoice.cadence.high} / ${invoice.cadence.total}` : '—'} highlight="#7c5cff" />
              <Row label="curated patterns (D1)" value={invoice?.curated_patterns.total_in_d1 || 0} highlight="#338650" />
              <Row label="記録優先候補 (deal>=5 未記録)" value={invoice?.curated_patterns.missing_partners_with_high_deals.length || 0} highlight={invoice && invoice.curated_patterns.missing_partners_with_high_deals.length >= 10 ? '#dd7a00' : undefined} />
              <Row label="重複検出 (30日)" value={invoice?.duplicate_detection.fired_30d || 0} />
              <Row label="prefill 採用率" value={invoice?.outcome_log.prefill_used_rate != null ? formatPct(invoice.outcome_log.prefill_used_rate) : '—'} highlight="#338650" />
              <Row label="outcome log total (30日)" value={invoice?.outcome_log.total || 0} />
            </tbody>
          </table>
        </div>
      </CardBase>

      {/* 統合 takeaway */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '8px 12px' }}>
          <SubSectionTitle headlineLevel={3}>状態評価</SubSectionTitle>
          <ul style={{ marginTop: '8px', fontSize: '0.85rem', color: '#332f2c' }}>
            {bank && bank.summary.ai_skip_rate_recent != null && bank.summary.ai_skip_rate_recent >= 0.5 && (
              <li>銀行: AI skip {formatPct(bank.summary.ai_skip_rate_recent)} = 機械化 ok</li>
            )}
            {bank && (bank.audit_violations.rule50_skips || 0) + (bank.audit_violations.rule51_skips || 0) > 0 && (
              <li style={{ color: '#c33939' }}>銀行: audit 違反 {(bank.audit_violations.rule50_skips || 0) + (bank.audit_violations.rule51_skips || 0)} 件 — 修復必要</li>
            )}
            {invoice && invoice.arica.registered_rate != null && invoice.arica.registered_rate >= 0.8 && (
              <li>請求書: ARICA 登録率 {formatPct(invoice.arica.registered_rate)} = 良好</li>
            )}
            {invoice && invoice.curated_patterns.missing_partners_with_high_deals.length >= 10 && (
              <li style={{ color: '#dd7a00' }}>請求書: 記録優先候補 {invoice.curated_patterns.missing_partners_with_high_deals.length} 件 — partner-patterns.md 追記推奨</li>
            )}
            {invoice && invoice.outcome_log.total > 0 && invoice.outcome_log.prefill_used_rate != null && invoice.outcome_log.prefill_used_rate < 0.3 && (
              <li style={{ color: '#dd7a00' }}>請求書: prefill 採用率 {formatPct(invoice.outcome_log.prefill_used_rate)} = curated patterns 拡充推奨</li>
            )}
          </ul>
        </div>
      </CardBase>
    </>
  );
};

const Row: React.FC<{ label: string; value: any; highlight?: string }> = ({ label, value, highlight }) => (
  <tr style={{ borderBottom: '1px solid #f0eeec' }}>
    <td style={{ padding: '6px 8px', color: '#6b6560', fontSize: '0.8125rem' }}>{label}</td>
    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: highlight || '#332f2c' }}>{value}</td>
  </tr>
);
